// src/services/reportes.ts
/**
 * Service para operaciones de Reportes y Dashboards.
 */
import { api } from './api';
import { PeriodoReporte } from '../types/reporte';
import type {
  Metrica,
  MetricaValor,
  Dashboard,
  DashboardCreate,
  DashboardUpdate,
  DashboardListItem,
  DashboardWidget,
  DashboardWidgetCreate,
  DashboardWidgetUpdate,
  ReporteRequest,
  ReporteResponse,
  ReporteGuardado,
  GuardarReporteRequest,
  ExportarReporteRequest,
  FiltrosReporte,
  TipoReporte,
  CategoriaMetrica,
} from '../types/reporte';

// Response types for specific reports
export interface ReporteOperativoResponse {
  nombre: string;
  fecha_inicio: string;
  fecha_fin: string;
  datos: Record<string, unknown>;
}

export interface ReporteGerencialResponse {
  nombre: string;
  periodo: string;
  datos: Record<string, unknown>;
}

export interface ReporteClienteResponse {
  nombre: string;
  cliente_id: string;
  periodo: string;
  datos: Record<string, unknown>;
}

export const reportesService = {
  // ==================== METRICAS ====================

  /**
   * Lista todas las métricas disponibles en el sistema.
   * @param categoria - Filtrar por categoria de metrica
   */
  listMetricas: (categoria?: CategoriaMetrica) => {
    const searchParams = new URLSearchParams();
    if (categoria) searchParams.set('categoria', categoria);
    const query = searchParams.toString();
    return api.get<Metrica[]>(`/reportes/metricas${query ? `?${query}` : ''}`);
  },

  /**
   * Obtiene el valor calculado de una métrica específica por su código.
   */
  getMetricaValor: (
    codigo: string,
    params?: {
      periodo?: PeriodoReporte;
      fecha_inicio?: string;
      fecha_fin?: string;
      cliente_id?: string;
    }
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.periodo) searchParams.set('periodo', params.periodo);
    if (params?.fecha_inicio) searchParams.set('fecha_inicio', params.fecha_inicio);
    if (params?.fecha_fin) searchParams.set('fecha_fin', params.fecha_fin);
    if (params?.cliente_id) searchParams.set('cliente_id', params.cliente_id);

    const query = searchParams.toString();
    return api.get<MetricaValor>(`/reportes/metricas/${codigo}${query ? `?${query}` : ''}`);
  },

  /**
   * Calcula múltiples métricas a la vez.
   */
  calcularMetricas: (data: {
    codigos: string[];
    periodo?: PeriodoReporte;
    filtros?: FiltrosReporte;
  }) => {
    const searchParams = new URLSearchParams();
    if (data.periodo) searchParams.set('periodo', data.periodo);
    const query = searchParams.toString();

    return api.post<MetricaValor[]>(
      `/reportes/metricas/calcular${query ? `?${query}` : ''}`,
      { codigos: data.codigos, filtros: data.filtros }
    );
  },

  // ==================== DASHBOARDS ====================

  /**
   * Lista todos los dashboards del usuario.
   * @param incluirCompartidos - Incluir dashboards compartidos por otros usuarios
   */
  listDashboards: (incluirCompartidos: boolean = true) => {
    const searchParams = new URLSearchParams();
    searchParams.set('incluir_compartidos', String(incluirCompartidos));
    return api.get<DashboardListItem[]>(`/reportes/dashboards?${searchParams.toString()}`);
  },

  /**
   * Crea un nuevo dashboard.
   */
  createDashboard: (data: DashboardCreate) =>
    api.post<Dashboard>('/reportes/dashboards', data),

  /**
   * Obtiene el dashboard por defecto del usuario con datos calculados.
   * @param periodo - Periodo para calcular los datos
   */
  getDefaultDashboard: (periodo?: PeriodoReporte) => {
    const searchParams = new URLSearchParams();
    if (periodo) searchParams.set('periodo', periodo);
    const query = searchParams.toString();
    return api.get<Dashboard>(`/reportes/dashboards/default${query ? `?${query}` : ''}`);
  },

  /**
   * Obtiene un dashboard específico por ID con datos calculados.
   * @param periodo - Periodo para calcular los datos
   */
  getDashboard: (id: string, periodo?: PeriodoReporte) => {
    const searchParams = new URLSearchParams();
    if (periodo) searchParams.set('periodo', periodo);
    const query = searchParams.toString();
    return api.get<Dashboard>(`/reportes/dashboards/${id}${query ? `?${query}` : ''}`);
  },

  /**
   * Actualiza un dashboard existente.
   */
  updateDashboard: (id: string, data: DashboardUpdate) =>
    api.put<Dashboard>(`/reportes/dashboards/${id}`, data),

  /**
   * Elimina un dashboard.
   */
  deleteDashboard: (id: string) => api.delete<{ message: string }>(`/reportes/dashboards/${id}`),

  /**
   * Agrega un widget a un dashboard.
   */
  addWidget: (dashboardId: string, data: DashboardWidgetCreate) =>
    api.post<DashboardWidget>(`/reportes/dashboards/${dashboardId}/widgets`, data),

  /**
   * Actualiza un widget de un dashboard.
   */
  updateWidget: (dashboardId: string, widgetId: string, data: DashboardWidgetUpdate) =>
    api.put<DashboardWidget>(`/reportes/dashboards/${dashboardId}/widgets/${widgetId}`, data),

  /**
   * Elimina un widget de un dashboard.
   */
  deleteWidget: (dashboardId: string, widgetId: string) =>
    api.delete<{ message: string }>(`/reportes/dashboards/${dashboardId}/widgets/${widgetId}`),

  /**
   * Establece un dashboard como el default del usuario.
   */
  setDefaultDashboard: (id: string) =>
    api.post<{ message: string }>(`/reportes/dashboards/${id}/default`),

  /**
   * Duplica un dashboard existente.
   * @param nombre - Nombre para el nuevo dashboard
   */
  duplicarDashboard: (id: string, nombre: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('nombre', nombre);
    return api.post<Dashboard>(`/reportes/dashboards/${id}/duplicar?${searchParams.toString()}`);
  },

  // ==================== REPORTES ====================

  /**
   * Genera un reporte con los parámetros especificados.
   */
  generarReporte: (data: ReporteRequest) =>
    api.post<ReporteResponse>('/reportes/generar', data),

  /**
   * Obtiene el reporte operativo (para equipos de soporte).
   * @param fechaInicio - Fecha de inicio del periodo
   * @param fechaFin - Fecha de fin del periodo
   * @param clienteId - Opcional: filtrar por cliente
   */
  getReporteOperativo: (
    fechaInicio: string,
    fechaFin: string,
    clienteId?: string
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.set('fecha_inicio', fechaInicio);
    searchParams.set('fecha_fin', fechaFin);
    if (clienteId) searchParams.set('cliente_id', clienteId);

    return api.get<ReporteOperativoResponse>(`/reportes/operativo?${searchParams.toString()}`);
  },

  /**
   * Obtiene el reporte gerencial (para gerentes y directivos).
   * @param periodo - Periodo para el reporte
   */
  getReporteGerencial: (periodo: PeriodoReporte = PeriodoReporte.MES) => {
    const searchParams = new URLSearchParams();
    searchParams.set('periodo', periodo);
    return api.get<ReporteGerencialResponse>(`/reportes/gerencial?${searchParams.toString()}`);
  },

  /**
   * Obtiene el reporte de un cliente específico.
   * @param clienteId - ID del cliente
   * @param periodo - Periodo para el reporte
   */
  getReporteCliente: (clienteId: string, periodo: PeriodoReporte = PeriodoReporte.MES) => {
    const searchParams = new URLSearchParams();
    searchParams.set('periodo', periodo);
    return api.get<ReporteClienteResponse>(`/reportes/cliente/${clienteId}?${searchParams.toString()}`);
  },

  /**
   * Lista los reportes guardados del usuario.
   * @param tipo - Opcional: filtrar por tipo de reporte
   */
  listReportesGuardados: (tipo?: TipoReporte) => {
    const searchParams = new URLSearchParams();
    if (tipo) searchParams.set('tipo', tipo);
    const query = searchParams.toString();
    return api.get<ReporteGuardado[]>(`/reportes/guardados${query ? `?${query}` : ''}`);
  },

  /**
   * Guarda un reporte generado para acceso futuro.
   * @param datos - Datos de la solicitud de guardado
   * @param reporteData - Datos del reporte generado
   */
  guardarReporte: (datos: GuardarReporteRequest, reporteData: ReporteResponse) =>
    api.post<ReporteGuardado>('/reportes/guardar', {
      ...datos,
      reporte_data: reporteData,
    }),

  /**
   * Elimina un reporte guardado.
   */
  deleteReporteGuardado: (id: string) =>
    api.delete<{ message: string }>(`/reportes/guardados/${id}`),

  /**
   * Exporta un reporte a formato PDF, Excel o CSV.
   */
  exportarReporte: (reporteId: string, data: ExportarReporteRequest) =>
    api.post<Blob>(`/reportes/${reporteId}/exportar`, data),
};
